import React from 'react';
import { PropInfo, CodeExample, TextFieldProps } from '@cimpress/react-components';
import TextAreaExample from './textAreaExample';
import TextAreaExampleCode from '!raw-loader!./textAreaExample.tsx';
import ComponentDoc from '../../shared/TabbedComponentDoc';

const TextFieldDocs = () => {
  const textFieldProps: PropInfo<TextFieldProps>[] = [
    {
      name: 'name',
      type: 'string',
      default: '',
      description: 'Name attribute for the input field',
    },
    {
      name: 'value',
      type: 'string or number',
      default: '',
      description: 'Input value of the text field',
    },
    {
      name: 'label',
      type: 'string',
      default: '',
      description: (
        <div>
          Floating label for an input field. Use either this or <code>placeholder</code> but not both
        </div>
      ),
    },
    {
      name: 'placeholder',
      type: 'string',
      default: '',
      description: (
        <div>
          Placeholder text for an input field. Use either this or <code>label</code> but not both
        </div>
      ),
    },
    {
      name: 'helpText',
      type: 'node',
      default: '',
      description: 'Additional help text to display under the input field.',
    },
    {
      name: 'id',
      type: 'string',
      default: '',
      description: 'ID prop for the input field',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      description: (
        <div>
          Marks the text field as required. Only applicable if a <code>label</code> is provided
        </div>
      ),
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      description: 'Marks the text field as disabled',
    },
    {
      name: 'autoFocus',
      type: 'boolean',
      default: 'false',
      description: 'Specifies that the text field focuses automatically',
    },
    {
      name: 'status',
      type: 'string',
      default: '',
      description: (
        <div>
          One of <code>success</code>, <code>warning</code>, or <code>error</code>, provides a validation state.
        </div>
      ),
    },
    {
      name: 'className',
      type: 'string',
      default: '',
      description: 'Additional classNames to be appended to the parent element of the input field',
    },
    {
      name: 'style',
      type: 'object',
      default: '',
      description: 'Inline styles to be applied to the root TextField div.',
    },
    {
      name: 'inputStyle',
      type: 'object',
      default: '',
      description: 'Styles to be applied to the input element.',
    },
    {
      name: 'onChange',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when the textfield's value changes. </p>
          <p>
            Signature: <code>function(event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>event:</code> Change event targeting the text field.
          </p>
        </div>
      ),
    },
    {
      name: 'onClick',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when the textfield is clicked. </p>
          <p>
            Signature: <code>function(event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>event:</code> Click event targeting the text field.
          </p>
        </div>
      ),
    },
    {
      name: 'onKeyDown',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when the textfield's key down event is fired. </p>
          <p>
            Signature: <code>function(event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>event:</code> Change event targeting the text field.
          </p>
        </div>
      ),
    },
    {
      name: 'onFocus',
      type: 'function',
      default: '',
      description: 'Callback function that is fired when the is focused.',
    },
    {
      name: 'onBlur',
      type: 'function',
      default: '',
      description: 'Callback function that is fired when the textfield loses focus.',
    },

    {
      name: 'rightAddon',
      type: 'element',
      default: '',
      description: 'Adds the ability to include a button contained on the right of the text field.',
    },
  ];

  return (
    <ComponentDoc name="Text Area" propInfos={textFieldProps}>
      <h5>Example of a Text Area</h5>
      <TextAreaExample />
      <CodeExample code={TextAreaExampleCode} />
    </ComponentDoc>
  );
};

export default TextFieldDocs;
